import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useMemberAuth } from "./MemberAuthContext";

// Global accessibility settings for the whole member PWA.
// Applies the three in-app options (high contrast, large text, reduced motion)
// by toggling classes on <html>, so CSS in index.css can react app-wide.
//
// PERSISTENCE MODEL (P1.5):
// localStorage stores ONLY the keys the member has CONSCIOUSLY chosen
// (a partial "choices" map), never a frozen snapshot of system-derived values.
// The effective setting for a key is: conscious choice if one exists, otherwise
// the current system preference (prefers-reduced-motion / prefers-contrast),
// otherwise the app default. This means: with no conscious choice the app follows
// the operating-system preferences on EVERY app start; a conscious choice (true OR
// false) is persisted and always wins. Tokens are never stored here.
const AccessibilityContext = createContext(null);

const DEFAULTS = { high_contrast: false, large_text: false, reduce_motion: false };
const KEYS = ["high_contrast", "large_text", "reduce_motion"];
const STORAGE_KEY = "visibilis_a11y_choices"; // partial map of conscious choices
const LEGACY_KEY = "visibilis_a11y"; // pre-P1.5 full snapshot (no longer used)

// Read the conscious-choice map, keeping only known boolean keys.
function readChoices() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) || {};
    const out = {};
    KEYS.forEach((k) => {
      if (typeof parsed[k] === "boolean") out[k] = parsed[k];
    });
    return out;
  } catch (_) {
    return {};
  }
}

// System preferences, evaluated fresh at each app start (mount). Used for any key
// that has no conscious choice. large_text has no system analogue -> default false.
function systemDefaults() {
  if (typeof window === "undefined" || !window.matchMedia) return {};
  return {
    reduce_motion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    high_contrast: window.matchMedia("(prefers-contrast: more)").matches,
  };
}

export function AccessibilityProvider({ children }) {
  const { member } = useMemberAuth();

  // Only conscious choices live in state/localStorage.
  const [choices, setChoices] = useState(readChoices);
  // System preferences captured once per app start.
  const [sysDefaults] = useState(systemDefaults);

  // Effective settings applied to the UI = defaults <- system <- conscious choices.
  const a11y = useMemo(
    () => ({ ...DEFAULTS, ...sysDefaults, ...choices }),
    [choices, sysDefaults],
  );

  // One-time cleanup of the legacy full-snapshot key.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch (_) {
      /* ignore */
    }
  }, []);

  // On login, a CONSCIOUSLY saved server setting (accessibility_choices[k]===true)
  // wins - true OR false - and is recorded as a conscious choice on this device so it
  // also survives a reload before the next login. Keys the member never chose are left
  // to follow the system. This preserves the accepted accessibility_choices priority.
  const memberId = member && member.id;
  useEffect(() => {
    if (!member) return;
    const srvAcc = (member.prefs && member.prefs.accessibility) || {};
    const srvChosen = (member.prefs && member.prefs.accessibility_choices) || {};
    const applied = {};
    KEYS.forEach((k) => {
      if (srvChosen[k] === true) applied[k] = !!srvAcc[k];
    });
    if (Object.keys(applied).length) {
      setChoices((prev) => ({ ...prev, ...applied }));
    }
  }, [memberId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect the effective settings onto the document root immediately.
  useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle("a11y-contrast", !!a11y.high_contrast);
    el.classList.toggle("a11y-large-text", !!a11y.large_text);
    el.classList.toggle("a11y-reduce-motion", !!a11y.reduce_motion);
  }, [a11y]);

  // Persist ONLY the conscious-choice map. When empty, remove the key entirely so a
  // future app start re-derives everything from the current system preferences.
  useEffect(() => {
    try {
      if (Object.keys(choices).length === 0) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify(choices));
    } catch (_) {
      /* ignore */
    }
  }, [choices]);

  // A toggle is always a conscious choice (true OR false): record + persist it.
  const setA11y = (next) => setChoices((prev) => ({ ...prev, ...next }));

  // Reset removes all conscious choices -> the app follows system preferences again.
  // The server-side reset (accessibility_choices -> {}) is done by the caller via the
  // existing prefs endpoint (no new backend/DB structure needed).
  const resetA11y = () => setChoices({});

  return (
    <AccessibilityContext.Provider value={{ a11y, setA11y, resetA11y }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error("useAccessibility must be used within AccessibilityProvider");
  return ctx;
}
